// Dynamic Blog-to-Social-Media Content Generator
// This system intelligently analyzes any blog content and generates relevant social media variants
// without hardcoding specific topics or industries

async function callLocalLLM(prompt, opts = {}) {
  console.log('[clientGenerator] Checking for LLM:', !!window.localLLM, typeof window.localLLM?.generate);
  
  if (window.localLLM && typeof window.localLLM.generate === 'function') {
    try {
      const testResult = await window.localLLM.generate('test', { maxTokens: 10 });
      console.log('[clientGenerator] LLM test result:', testResult);
      
      // Enhanced demo LLM detection
      if (testResult && (
        testResult.includes('Demo LLM') || 
        testResult.includes('no real model loaded') ||
        testResult.includes('placeholder') ||
        testResult.includes('demo-model')
      )) {
        console.log('[clientGenerator] Demo LLM detected, using fallback generation instead');
        throw new Error('demo-llm-detected');
      }
      
      console.log('[clientGenerator] Real LLM detected, proceeding with LLM generation');
      return await window.localLLM.generate(prompt, opts);
    } catch (e) {
      console.log('[clientGenerator] LLM test/generation failed:', e.message);
      throw new Error('llm-test-failed');
    }
  }
  console.log('[clientGenerator] No LLM available, using fallback generation');
  throw new Error('no-local-llm');
}

// Intelligent problem extraction that works with any blog content
async function extractProblemSolution(blog) {
  console.log('[clientGenerator] Starting intelligent problem extraction for:', blog.title);
  
  // Try LLM extraction first
  try {
    const prompt = `Analyze this blog content and extract 3 real user problems it addresses. For each problem, provide how Coooly solves it. Output ONLY valid JSON: {"problems":["..."], "solutions":["..."]}. Blog: ${JSON.stringify(blog)}`;
    const out = await callLocalLLM(prompt, { maxTokens: 256 });
    try {
      const parsed = JSON.parse(out);
      if (parsed && Array.isArray(parsed.problems) && Array.isArray(parsed.solutions)) {
        return { problems: parsed.problems.slice(0,3), solutions: parsed.solutions.slice(0,3) };
      }
    } catch (e) {
      console.log('[clientGenerator] LLM JSON parsing failed:', e.message);
    }
  } catch (e) {
    console.log('[clientGenerator] LLM failed for problem extraction, using intelligent heuristic fallback');
  }

  // Intelligent dynamic problem extraction
  const content = (blog.content || blog.body || blog.excerpt || '').replace(/<[^>]*>/g, ' ');
  const title = blog.title || '';
  const allText = (title + ' ' + content).toLowerCase();
  
  console.log('[clientGenerator] Analyzing content for context:', allText.slice(0, 200) + '...');
  console.log('[clientGenerator] Content includes "recruitment":', allText.includes('recruitment'));
  console.log('[clientGenerator] Content includes "hiring":', allText.includes('hiring'));
  console.log('[clientGenerator] Content includes "free":', allText.includes('free'));
  console.log('[clientGenerator] Content includes "job posting":', allText.includes('job posting'));

  // Intelligent content-based problem extraction
  const problems = [];
  
  // Method 1: Analyze for recruitment/hiring context
  if (allText.includes('recruitment') || allText.includes('hiring') || allText.includes('job posting')) {
    if (allText.includes('free')) {
      problems.push(`Tired of paying high recruitment fees?`);
      problems.push(`Need qualified candidates without the cost?`);
      problems.push(`Want to hire great talent on a budget?`);
    } else {
      problems.push(`Struggling to find the right candidates?`);
      problems.push(`Having trouble with your hiring process?`);
      problems.push(`Need better recruitment strategies?`);
    }
  }
  
  // Method 2: Analyze for practice management context
  else if (allText.includes('practice') && (allText.includes('dental') || allText.includes('medical'))) {
    problems.push(`Struggling with practice efficiency?`);
    problems.push(`Need better patient management tools?`);
    problems.push(`Want to streamline your operations?`);
  }
  
  // Method 3: Analyze for efficiency/automation context  
  else if (allText.includes('automat') || allText.includes('streamline') || allText.includes('efficiency')) {
    problems.push(`Spending too much time on manual tasks?`);
    problems.push(`Need to improve operational efficiency?`);
    problems.push(`Want to automate repetitive processes?`);
  }
  
  // Method 4: Analyze for financial/cost context
  else if (allText.includes('save money') || allText.includes('reduce cost') || allText.includes('expensive')) {
    problems.push(`Looking to reduce operational costs?`);
    problems.push(`Need more cost-effective solutions?`);
    problems.push(`Want to maximize your budget?`);
  }
  
  // Method 5: Analyze for growth/scaling context
  else if (allText.includes('grow') || allText.includes('scale') || allText.includes('expand')) {
    problems.push(`Ready to scale your business?`);
    problems.push(`Need tools for sustainable growth?`);
    problems.push(`Want to expand efficiently?`);
  }
  
  // Method 6: Generic business improvement (fallback)
  else {
    problems.push(`Looking to improve your business operations?`);
    problems.push(`Need professional tools and guidance?`);
    problems.push(`Want to optimize your workflows?`);
  }

  // Generate solutions dynamically based on content context
  const solutions = problems.map(problem => generateDynamicSolution(problem, allText, blog));

  const finalProblems = [...new Set(problems)].slice(0, 3);
  const finalSolutions = solutions.slice(0, 3);

  console.log('[clientGenerator] ✅ SMART problems generated:', finalProblems);
  console.log('[clientGenerator] ✅ Context-aware solutions:', finalSolutions);

  return { problems: finalProblems, solutions: finalSolutions };
}

// Extract meaningful concepts from any content
function extractContentConcepts(text) {
  const concepts = [];
  
  // Industry-agnostic concept patterns
  const conceptPatterns = [
    /\b(management|automation|efficiency|productivity|growth|revenue|income|savings|optimization|analytics|reporting|tracking|insights|performance|quality|satisfaction|experience|workflow|processes|operations|strategy|planning|marketing|sales|customer|client|service|support|technology|software|tools|platform|solution|system)\b/g,
    /\b(team|staff|employees|hiring|recruitment|talent|professionals|candidates|training|development|skills|expertise|knowledge|education|learning|communication|collaboration|leadership|organization)\b/g
  ];
  
  conceptPatterns.forEach(pattern => {
    const matches = text.match(pattern) || [];
    concepts.push(...matches);
  });
  
  return [...new Set(concepts)].slice(0, 5);
}

// Extract action words from content
function extractActionWords(text) {
  const actionPattern = /\b(improve|increase|boost|grow|build|create|develop|manage|optimize|streamline|automate|enhance|discover|learn|master|achieve|reach|gain|save|reduce|eliminate|solve|fix|handle|attract|find|hire|recruit|track|monitor|analyze|report|organize|plan|implement|execute|deliver|provide|offer|enable|help|assist|support|guide|teach|train)\b/g;
  const matches = text.match(actionPattern) || [];
  return [...new Set(matches)].slice(0, 5);
}

// Extract benefits mentioned in content
function extractBenefits(text) {
  const benefits = [];
  
  const benefitPatterns = [
    /\b(?:save|saving)\s+(time|money|hours|costs?|resources?)\b/gi,
    /\b(?:increase|increasing|boost|boosting)\s+(revenue|income|profits?|efficiency|productivity|sales|growth)\b/gi,
    /\b(?:improve|improving|enhance|enhancing)\s+(quality|performance|satisfaction|experience|results|outcomes)\b/gi,
    /\b(?:reduce|reducing|eliminate|eliminating)\s+(costs?|errors?|waste|time|effort|complexity)\b/gi,
    /\b(?:streamline|automate|optimize)\s+(processes|workflows?|operations)\b/gi
  ];
  
  benefitPatterns.forEach(pattern => {
    const matches = text.match(pattern) || [];
    benefits.push(...matches);
  });
  
  return [...new Set(benefits)].slice(0, 3);
}

// Extract pain points from content
function extractPainPoints(text) {
  const painPoints = [];
  
  const painPatterns = [
    /\bproblem(?:s)?\s+(?:with|of)\s+([a-z\s]{10,40})\b/gi,
    /\bstruggl(?:e|es|ing)\s+(?:with|to)\s+([a-z\s]{10,40})\b/gi,
    /\bdifficult(?:y|ies)?\s+(?:with|in)\s+([a-z\s]{10,40})\b/gi,
    /\bchallenges?\s+(?:with|of|in)\s+([a-z\s]{10,40})\b/gi,
    /\bissues?\s+(?:with|in)\s+([a-z\s]{10,40})\b/gi,
    /\bwast(?:e|ing)\s+(time|money|resources?|effort)\b/gi,
    /\bexpensive\s+([a-z\s]{10,30})\b/gi
  ];
  
  painPatterns.forEach(pattern => {
    let match;
    while ((match = pattern.exec(text)) !== null) {
      if (match[1]) {
        painPoints.push(match[1].trim());
      }
    }
  });
  
  return [...new Set(painPoints)].slice(0, 3);
}

// Generate intelligent solutions based on problem and content context
function generateDynamicSolution(problem, contentText, blog) {
  const contentLower = contentText.toLowerCase();
  
  // Recruitment/hiring solutions
  if (contentLower.includes('recruitment') || contentLower.includes('hiring')) {
    if (contentLower.includes('free')) {
      return 'Post jobs on Coooly completely FREE and connect directly with qualified dental professionals without recruitment fees.';
    } else {
      return 'Find top talent efficiently with Coooly\'s targeted recruitment platform designed for dental practices.';
    }
  }
  
  // Practice management solutions
  else if (contentLower.includes('practice') && (contentLower.includes('dental') || contentLower.includes('medical'))) {
    return 'Streamline your practice with Coooly\'s comprehensive management tools for scheduling, billing, and patient care.';
  }
  
  // Efficiency/automation solutions
  else if (contentLower.includes('automat') || contentLower.includes('efficiency') || contentLower.includes('streamline')) {
    return 'Automate routine tasks and boost productivity with Coooly\'s intelligent workflow optimization tools.';
  }
  
  // Cost/financial solutions
  else if (contentLower.includes('save') || contentLower.includes('cost') || contentLower.includes('free')) {
    return 'Reduce operational costs and maximize your budget with Coooly\'s cost-effective professional solutions.';
  }
  
  // Growth/scaling solutions
  else if (contentLower.includes('grow') || contentLower.includes('scale') || contentLower.includes('expand')) {
    return 'Scale your business confidently with Coooly\'s growth-focused tools and professional guidance.';
  }
  
  // Generic professional solution
  else {
    return 'Transform your business operations with Coooly\'s comprehensive professional platform and expert support.';
  }
}

// Enhanced variant generation using dynamic problem extraction
async function generateVariants(blog, templateId, opts = {}) {
  console.log('[clientGenerator] Generating variants for blog:', blog.title);
  
  // Import templates
  const templates = await import('./blogTemplates.json').then(m => m.default || m);
  const template = templates[templateId] || Object.values(templates)[0];

  // Extract problems and solutions intelligently
  const ps = await extractProblemSolution(blog);
  
  // Try LLM generation first
  try {
    const llmVariants = await generateLLMVariants(blog, ps, template, opts);
    if (llmVariants && llmVariants.length > 0) {
      console.log('[clientGenerator] Successfully generated LLM variants:', llmVariants.length);
      return llmVariants;
    }
  } catch (e) {
    console.log('[clientGenerator] LLM variant generation failed, using intelligent fallback');
  }

  // Intelligent fallback generation
  return generateIntelligentFallbackVariants(blog, ps, template, opts);
}

// LLM variant generation with improved prompts
async function generateLLMVariants(blog, problemSolutions, template, opts) {
  const prompt = `You are a social media expert creating engaging posts for Coooly, a professional platform.

Create 3 social media variants for this blog post: "${blog.title}"

Problems this blog addresses: ${problemSolutions.problems.join(', ')}
How Coooly helps: ${problemSolutions.solutions.join(' ')}

Each variant needs:
- Title: Engaging question or statement (max 60 chars)
- Message: Compelling description of value (max 200 chars) 
- CTA: Strong call-to-action (max 20 chars)
- Hashtags: 3-5 relevant tags

Output ONLY valid JSON:
{
  "variants": [
    {"title": "...", "message": "...", "cta": "...", "hashtags": ["#tag1", "#tag2"]},
    {"title": "...", "message": "...", "cta": "...", "hashtags": ["#tag1", "#tag2"]},
    {"title": "...", "message": "...", "cta": "...", "hashtags": ["#tag1", "#tag2"]}
  ]
}`;

  const result = await callLocalLLM(prompt, { maxTokens: 512, temperature: 0.7 });
  
  try {
    const parsed = JSON.parse(result);
    if (parsed && parsed.variants && Array.isArray(parsed.variants)) {
      return parsed.variants.slice(0, 3).map(normalizeVariant);
    }
  } catch (e) {
    console.log('[clientGenerator] LLM variant JSON parsing failed:', e.message);
  }
  
  throw new Error('llm-variant-generation-failed');
}

// Intelligent fallback generation (no hardcoding)
function generateIntelligentFallbackVariants(blog, problemSolutions, template, opts) {
  console.log('[clientGenerator] Generating intelligent fallback variants');
  
  const problems = problemSolutions.problems || [];
  const solutions = problemSolutions.solutions || [];
  
  const variants = [];
  
  // Generate 3 variants from the extracted problems
  for (let i = 0; i < 3; i++) {
    const problem = problems[i] || problems[0] || 'Want to improve your business?';
    const solution = solutions[i] || solutions[0] || 'Coooly provides professional tools and guidance.';
    
    const variant = {
      title: createEngagingTitle(problem, i),
      message: createCompellingMessage(solution, blog, i),
      cta: generateContextualCTA(problem, i),
      hashtags: generateRelevantHashtags(blog, problem, i),
      imageHint: template.imageHint || '',
      templateId: template.id,
      confidence: 0.7 - i * 0.1
    };
    
    variants.push(normalizeVariant(variant));
  }
  
  console.log('[clientGenerator] Generated intelligent fallback variants:', variants.length);
  return variants;
}

// Create engaging titles from problems
function createEngagingTitle(problem, index) {
  // If problem is already a question, use it
  if (problem.includes('?')) {
    return safeTruncate(problem, 60);
  }
  
  // Transform statement problems into questions
  const titleVariations = [
    problem,
    problem.replace(/^struggling with/i, 'Need help with') + '?',
    problem.replace(/^need/i, 'Looking for') + '?',
    problem.replace(/^want/i, 'Ready') + '?'
  ];
  
  return safeTruncate(titleVariations[index % titleVariations.length], 60);
}

// Create compelling messages from solutions
function createCompellingMessage(solution, blog, index) {
  const messageVariations = [
    solution,
    `Discover how ${solution.toLowerCase()}`,
    `Join thousands who ${solution.toLowerCase()}`,
    `Transform your business: ${solution.toLowerCase()}`
  ];
  
  return safeTruncate(messageVariations[index % messageVariations.length], 200);
}

// Generate contextual CTAs
function generateContextualCTA(problem, index) {
  const ctas = [];
  
  // Context-based CTAs
  if (problem.includes('find') || problem.includes('discover')) {
    ctas.push('Find Out How', 'Discover More', 'Learn More');
  } else if (problem.includes('save') || problem.includes('reduce')) {
    ctas.push('Save Time Now', 'Start Saving', 'Get Started');
  } else if (problem.includes('improve') || problem.includes('enhance')) {
    ctas.push('Improve Today', 'Enhance Now', 'Get Better Results');
  } else if (problem.includes('grow') || problem.includes('increase')) {
    ctas.push('Grow Now', 'Start Growing', 'Boost Results');
  } else {
    ctas.push('Try Coooly Free', 'Get Started Today', 'Learn More');
  }
  
  return ctas[index % ctas.length];
}

// Generate relevant hashtags
function generateRelevantHashtags(blog, problem, index) {
  const hashtags = [];
  
  // Extract hashtags from blog tags if available
  if (blog.tags && blog.tags.length > 0) {
    hashtags.push(...blog.tags.slice(0, 3).map(tag => `#${tag.replace(/[^a-z0-9]/gi, '')}`));
  }
  
  // Add problem-based hashtags
  if (problem.includes('hire') || problem.includes('recruit')) {
    hashtags.push('#hiring', '#recruitment', '#talent');
  } else if (problem.includes('patient')) {
    hashtags.push('#patientcare', '#healthcare', '#dental');
  } else if (problem.includes('efficiency') || problem.includes('productivity')) {
    hashtags.push('#efficiency', '#productivity', '#automation');
  } else if (problem.includes('grow') || problem.includes('business')) {
    hashtags.push('#growth', '#business', '#success');
  } else {
    hashtags.push('#business', '#professional', '#success');
  }
  
  // Add Coooly branding
  hashtags.push('#coooly');
  
  return [...new Set(hashtags)].slice(0, 5);
}

// Utility functions
function normalizeVariant(v) {
  return {
    title: safeTruncate(v.title || '', 60),
    message: safeTruncate(v.message || '', 200),
    cta: safeTruncate(v.cta || 'Learn More', 20),
    hashtags: (v.hashtags || []).slice(0, 5),
    imageHint: v.imageHint || '',
    templateId: v.templateId || '',
    confidence: v.confidence || 0.5
  };
}

function safeTruncate(str, max) {
  if (!str) return '';
  if (str.length <= max) return str;
  return str.slice(0, Math.max(0, max - 1)).trim() + '…';
}

// Export functions for external use
export default { generateVariants, extractProblemSolution };
